import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import useInView from '../../hooks/useInView';

const INDUSTRIES = [
  {
    icon: 'ri-store-2-line',
    title: 'Retail & E-Commerce',
    tagline: 'Visibility, traffic, and conversions',
    desc: 'Whether you sell products online or in-store, digital marketing helps you reach more buyers, reduce cart abandonment, and improve your return on ad spend. We help retailers build organic visibility through SEO, run performance-driven paid campaigns, and develop social media strategies that drive purchase intent.',
    what: ['Product-focused SEO', 'Google Shopping & Meta Ads', 'Retargeting campaigns', 'Social content strategy'],
    color: 'text-orange-500',
    accent: 'bg-orange-500',
    bg: 'bg-orange-50 border-orange-100',
    light: true,
  },
  {
    icon: 'ri-building-2-line',
    title: 'Real Estate',
    tagline: 'Qualified leads for developers and agents',
    desc: 'In a market driven by trust and visibility, digital presence makes a significant difference. We help real estate businesses generate qualified leads, build brand credibility, and reach buyers and investors through targeted campaigns and content that positions them as reliable names in their market.',
    what: ['Lead generation campaigns', 'Property-specific landing pages', 'Google & Meta Ads', 'Local SEO & GMB optimization'],
    color: 'text-sky-500',
    accent: 'bg-sky-500',
    bg: 'bg-sky-50 border-sky-100',
    light: false,
  },
  {
    icon: 'ri-hospital-line',
    title: 'Healthcare & Wellness',
    tagline: 'Trust, reach, and patient acquisition',
    desc: 'Healthcare requires careful, compliant marketing that builds trust before it asks for action. We help clinics, practitioners, and wellness brands reach local patients, build genuine authority online, and grow their practice through strategies that are both effective and appropriate for the sector.',
    what: ['Local SEO & map visibility', 'Patient-focused content', 'Review management strategy', 'Social media presence'],
    color: 'text-brand-blue-light',
    accent: 'bg-brand-blue-light',
    bg: 'bg-blue-50 border-blue-100',
    light: true,
  },
  {
    icon: 'ri-graduation-cap-line',
    title: 'Education & EdTech',
    tagline: 'Reach students and grow enrolments',
    desc: 'From schools and institutes to online learning platforms, education brands need to reach the right students at the right stage of their decision journey. We build awareness through content and social media, capture demand through paid ads, and help education brands communicate their value clearly.',
    what: ['Enrolment-focused campaigns', 'Course & program SEO', 'Meta & Google Ads', 'Community building on social'],
    color: 'text-violet-500',
    accent: 'bg-violet-500',
    bg: 'bg-violet-50 border-violet-100',
    light: false,
  },
  {
    icon: 'ri-restaurant-line',
    title: 'Food & Hospitality',
    tagline: 'Local presence and customer retention',
    desc: 'Restaurants, hotels, and hospitality businesses thrive when people can find them easily and trust what they see. We help food and hospitality brands build a stronger local and social presence, respond well to reviews, and create content that makes people want to visit.',
    what: ['Local SEO & Google Maps', 'Instagram & Facebook management', 'Paid local campaigns', 'Content that drives footfall'],
    color: 'text-yellow-500',
    accent: 'bg-yellow-500',
    bg: 'bg-yellow-50 border-yellow-100',
    light: true,
  },
  {
    icon: 'ri-briefcase-4-line',
    title: 'B2B & Professional Services',
    tagline: 'Authority, leads, and long sales cycles',
    desc: 'B2B buying decisions take time. Your digital presence needs to build credibility before your sales team picks up the phone. We help consultants, agencies, and service businesses generate qualified leads, create thought leadership content, and communicate their expertise clearly online.',
    what: ['LinkedIn strategy & ads', 'SEO for B2B keywords', 'Lead generation funnels', 'Case study and content marketing'],
    color: 'text-brand-blue',
    accent: 'bg-brand-blue',
    bg: 'bg-blue-50 border-blue-100',
    light: false,
  },
  {
    icon: 'ri-home-4-line',
    title: 'Home & Lifestyle',
    tagline: 'Audience growth and enquiry generation',
    desc: 'Interior design, furniture, and home improvement brands have highly visual products that perform well on social media — but only when the strategy and execution are right. We help home and lifestyle brands grow their audience, create compelling content, and convert engagement into real business enquiries.',
    what: ['Instagram & Pinterest strategy', 'Visual content creation', 'Paid social campaigns', 'SEO for home-related searches'],
    color: 'text-rose-500',
    accent: 'bg-rose-500',
    bg: 'bg-rose-50 border-rose-100',
    light: true,
  },
  {
    icon: 'ri-rocket-line',
    title: 'Startups & Early-Stage Brands',
    tagline: 'Building presence from scratch',
    desc: 'New businesses need to establish a digital foundation before scaling. We help early-stage brands build their online presence strategically — starting with the basics that matter most and avoiding the costly mistakes that come from doing everything at once without a clear direction.',
    what: ['Brand identity and positioning', 'Website development', 'Initial SEO setup', 'First paid campaign management'],
    color: 'text-cyan-500',
    accent: 'bg-cyan-500',
    bg: 'bg-cyan-50 border-cyan-100',
    light: false,
  },
];

function IndustriesHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 80); return () => clearTimeout(t); }, []);
  return (
    <section className="relative bg-white pt-32 pb-20 overflow-hidden border-b border-gray-100">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-transparent pointer-events-none"></div>
      <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6">
        <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.7s ease 100ms, transform 0.7s ease 100ms' }}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-blue border border-brand-blue/30 bg-brand-blue/10 px-4 py-1.5 rounded-full mb-7">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-blue animate-pulse"></span>
            Who We Work With
          </span>
          <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl text-gray-900 leading-tight max-w-3xl mb-6">
            Industries We Help
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl leading-relaxed">
            We work across a range of sectors. Each industry has its own challenges and opportunities — here's how we approach each one.
          </p>
        </div>
      </div>
    </section>
  );
}

function IndustrySection({ industry, idx }: { industry: typeof INDUSTRIES[0]; idx: number }) {
  const { ref, visible } = useInView(0.1);
  const bg = idx % 2 === 0 ? 'bg-white' : 'bg-gray-50';

  return (
    <section className={`py-20 md:py-24 ${bg} border-b border-gray-100`}>
      <div className="max-w-site mx-auto px-4 sm:px-6">
        <div
          ref={ref}
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transition: 'opacity 0.65s ease, transform 0.65s ease' }}
        >
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start ${idx % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
            <div className={idx % 2 === 1 ? 'lg:col-start-2' : ''}>
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl border ${industry.bg} transition-transform duration-300 hover:scale-110`}>
                  <i className={`${industry.icon} text-xl ${industry.color}`}></i>
                </div>
              </div>
              <div className="flex items-start gap-3 mb-2">
                <span className="font-display font-bold text-6xl leading-none select-none text-gray-200">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <h2 className="font-display font-bold text-2xl md:text-3xl text-gray-900 leading-tight pt-2">
                  {industry.title}
                </h2>
              </div>
              <p className="text-gray-500 text-sm font-semibold uppercase tracking-wide mb-5">{industry.tagline}</p>
              <p className="text-gray-600 text-[15px] leading-relaxed">{industry.desc}</p>
            </div>
            <div className={idx % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
              <p className={`text-[11px] font-bold uppercase tracking-widest ${industry.color} mb-4`}>What We Focus On</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {industry.what.map(w => (
                  <div key={w} className="flex items-center gap-3 rounded-xl border bg-white border-gray-200 hover:border-gray-300 px-4 py-3.5 transition-all duration-200 hover:-translate-y-0.5 cursor-default">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${industry.accent}`}></span>
                    <span className="text-gray-700 text-sm font-medium">{w}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function IndustriesCTA() {
  const { ref, visible } = useInView(0.2);
  return (
    <section className="py-20 bg-blue-50/50 border-t border-gray-100">
      <div ref={ref} className="max-w-site mx-auto px-4 sm:px-6 text-center" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}>
        <h2 className="font-display font-bold text-3xl md:text-4xl text-gray-900 mb-5">Your Industry Isn't Listed?</h2>
        <p className="text-gray-600 text-[15px] max-w-md mx-auto mb-8 leading-relaxed">
          The principles of good digital marketing apply broadly. Reach out and we'll have an honest conversation about whether we can help.
        </p>
        <Link to="/contact" className="btn-primary text-base px-8 py-4">
          Get In Touch
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    </section>
  );
}

export default function IndustriesPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <IndustriesHero />
      {INDUSTRIES.map((industry, idx) => (
        <IndustrySection key={industry.title} industry={industry} idx={idx} />
      ))}
      <IndustriesCTA />
      <Footer />
    </div>
  );
}
